import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowRight,
  FiBarChart2,
  FiCheck,
  FiChevronDown,
  FiDollarSign,
  FiExternalLink,
  FiFileText,
  FiGlobe,
  FiHome,
  FiLinkedin,
  FiLock,
  FiUsers,
  FiZap,
} from 'react-icons/fi';
import { loginlogo, platformHeroBanner } from '../../../components/imagepath';
import ClientsMap from '../../../components/Platform/ClientsMap';
import publicPlanService, { DEFAULT_PLANS } from '../../../services/publicPlanService';
import { useLandingAnimations } from '../../../hooks/useLandingAnimations';

const HERO_BG_DEEP = '#040b4f';

const PLAN_COPY = {
  profesional: {
    tagline: '1 mes gratis para probar con 1 edificio completo',
    cta: 'Probar 1 mes gratis',
    featured: true,
    trialMonths: 1,
  },
  enterprise: {
    tagline: 'Para administradores con varios edificios en cartera',
    cta: 'Solicitar Enterprise',
    featured: false,
  },
};

const formatPlanPrice = (price) => {
  const amount = Number(price);
  if (!amount) return { main: 'Gratis', suffix: '' };
  return { main: `S/ ${amount.toFixed(2)}`, suffix: '/ mes' };
};

const formatBuildingLimit = (value) => {
  if (value === 1) return '1 edificio en tu cuenta';
  return `Hasta ${value} edificios en tu cuenta`;
};

const formatStorage = (mb) => {
  if (mb >= 1024) {
    const gb = mb / 1024;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB para documentos`;
  }
  return `${mb} MB para documentos`;
};

const planFeatures = (plan) => [
  formatBuildingLimit(plan.max_inmuebles),
  'Pisos, departamentos y locales sin límite por edificio',
  `Hasta ${plan.max_usuarios} usuarios en tu equipo`,
  formatStorage(plan.max_storage_mb),
  'Pagos, gastos y reportes incluidos',
];

const BrandLogo = ({ onDark = false, className = '' }) => {
  const img = (
    <img src={loginlogo} alt="RentAHAB" className={`h-8 w-auto object-contain ${className}`} />
  );
  if (onDark) {
    return (
      <span className="inline-flex items-center bg-white rounded-xl px-3 py-2 shadow-lg shadow-black/25">
        {img}
      </span>
    );
  }
  return img;
};

const features = [
  {
    icon: FiHome,
    title: 'Gestión de edificios',
    description: 'Registra tus inmuebles con pisos, departamentos, locales y áreas comunes en un solo catálogo.',
    accent: 'from-blue-500/20 to-cyan-500/10',
    span: 'md:col-span-2 md:row-span-1',
  },
  {
    icon: FiUsers,
    title: 'Inquilinos y contratos',
    description: 'Lleva el historial de cada inquilino, sus contratos de alquiler y documentación asociada.',
    accent: 'from-violet-500/20 to-purple-500/10',
    span: '',
  },
  {
    icon: FiDollarSign,
    title: 'Pagos y cobranza',
    description: 'Controla rentas, vencimientos, pagos recibidos y el estado de cuenta por espacio.',
    accent: 'from-emerald-500/20 to-teal-500/10',
    span: '',
  },
  {
    icon: FiFileText,
    title: 'Gastos del inmueble',
    description: 'Registra mantenimiento, servicios y egresos por edificio para tener la contabilidad al día.',
    accent: 'from-amber-500/20 to-orange-500/10',
    span: 'md:col-span-2',
  },
  {
    icon: FiBarChart2,
    title: 'Reportes y decisiones',
    description: 'Consulta ingresos, morosidad y resultados por propiedad para tomar mejores decisiones.',
    accent: 'from-indigo-500/20 to-blue-500/10',
    span: 'md:col-span-3',
  },
];

const steps = [
  {
    num: '01',
    icon: FiHome,
    title: 'Registra tus inmuebles',
    text: 'Carga tus edificios, define pisos y espacios disponibles para alquiler.',
  },
  {
    num: '02',
    icon: FiUsers,
    title: 'Vincula inquilinos y contratos',
    text: 'Asocia cada espacio con su inquilino, contrato y condiciones de pago.',
  },
  {
    num: '03',
    icon: FiBarChart2,
    title: 'Opera con control total',
    text: 'Gestiona cobros, gastos y reportes desde un panel pensado para el día a día.',
  },
];

const serviceHighlights = [
  'Pagos y gastos por edificio',
  'Contratos y documentación centralizada',
  'Reportes listos para tu equipo',
  'Acceso para administradores y personal autorizado',
];

const trustItems = [
  'Varios edificios en un solo lugar',
  'Cobranza y gastos bajo control',
  'Información siempre actualizada',
];

const PlatformLanding = () => {
  const [mapStats, setMapStats] = useState({ buildings: 0, clients: 0, cities: 0 });
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [navScrolled, setNavScrolled] = useState(false);
  const { rootRef, setStatRef } = useLandingAnimations(mapStats);

  useEffect(() => {
    publicPlanService.fetchPlans().then(setPlans).catch(() => setPlans(DEFAULT_PLANS));
  }, []);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 48);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div ref={rootRef} className="min-h-screen bg-[#f8f9fc] text-slate-800 overflow-x-hidden">
      {/* Nav fijo */}
      <header
        data-hero-nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          navScrolled
            ? 'bg-slate-950/85 backdrop-blur-xl border-b border-white/10 shadow-2xl shadow-black/20 py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0">
            <BrandLogo onDark />
          </Link>
          <nav className="flex items-center gap-4 sm:gap-8">
            <a
              href="#servicios"
              className="hidden sm:inline text-sm text-white/75 hover:text-white transition-colors"
            >
              Servicios
            </a>
            <a
              href="#planes"
              className="hidden md:inline text-sm text-white/75 hover:text-white transition-colors"
            >
              Planes
            </a>
            <a
              href="#como-funciona"
              className="hidden sm:inline text-sm text-white/75 hover:text-white transition-colors"
            >
              Cómo funciona
            </a>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/25 backdrop-blur-sm px-4 py-2 rounded-full transition-all"
            >
              <FiLock className="w-3.5 h-3.5" />
              Acceso admin
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative min-h-screen flex flex-col justify-center text-white overflow-hidden"
        style={{ backgroundColor: HERO_BG_DEEP }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={platformHeroBanner}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full min-h-full min-w-full object-cover object-left scale-105"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#040b4f]/90 via-[#040b4f]/45 to-[#040b4f]/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#040b4f]/90 via-[#040b4f]/30 to-transparent" />
        {/* Relleno lateral derecho en pantallas ultra-anchas */}
        <div
          className="absolute inset-y-0 right-0 w-[30%] hidden 2xl:block pointer-events-none"
          style={{
            background: 'linear-gradient(to left, #0a1a5c 0%, #0a1a5c 40%, transparent 100%)',
          }}
        />

        <div
          data-orb
          className="absolute top-1/4 right-[12%] w-72 h-72 rounded-full bg-cyan-400/20 blur-3xl pointer-events-none"
        />
        <div
          data-orb
          className="absolute bottom-1/3 left-[8%] w-56 h-56 rounded-full bg-rentahab-500/25 blur-3xl pointer-events-none"
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 sm:py-32 w-full">
          <span
            data-hero-badge
            className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] bg-white/10 border border-white/20 backdrop-blur-md rounded-full px-4 py-2 mb-8"
          >
            <FiZap className="w-3.5 h-3.5 text-cyan-300" />
            Gestión integral de alquileres
          </span>

          <h1
            data-hero-title
            className="text-white text-4xl sm:text-5xl lg:text-6xl xl:text-[4.25rem] font-black leading-[1.05] max-w-4xl tracking-tight"
          >
            Tu administración de alquileres, ordenada y bajo control
          </h1>

          <p
            data-hero-desc
            className="mt-6 text-lg sm:text-xl text-white/75 max-w-2xl leading-relaxed font-light"
          >
            RentAHAB ayuda a inmobiliarias y administradores de edificios a gestionar inmuebles,
            inquilinos, pagos y gastos sin depender de planillas ni procesos dispersos.
          </p>

          <div data-hero-cta className="mt-10 flex flex-wrap gap-4">
            <a
              href="#servicios"
              className="group inline-flex items-center gap-2 bg-white text-rentahab-700 font-bold px-7 py-3.5 rounded-full hover:shadow-glow hover:scale-[1.02] transition-all text-sm"
            >
              Conocer más
              <FiChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
            </a>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 border-2 border-white/35 text-white font-bold px-7 py-3.5 rounded-full hover:bg-white/10 backdrop-blur-sm transition-all text-sm"
            >
              Acceso clientes
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div data-hero-trust className="mt-12 flex flex-wrap gap-x-6 gap-y-3">
            {trustItems.map((item) => (
              <span key={item} className="inline-flex items-center gap-2 text-sm text-white/60">
                <FiCheck className="w-4 h-4 text-emerald-400" />
                {item}
              </span>
            ))}
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40 text-xs">
          <span>Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
        </div>
      </section>

      {/* Features — bento grid */}
      <section id="servicios" data-features className="py-24 sm:py-32 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(46,55,164,0.06)_0%,_transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-2xl mb-16">
            <p className="text-rentahab-500 font-bold text-sm uppercase tracking-widest mb-3">
              Nuestros servicios
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight">
              ¿Qué puedes hacer con RentAHAB?
            </h2>
            <p className="mt-4 text-slate-500 text-lg leading-relaxed">
              Centraliza la operación de tus alquileres: desde el inventario de espacios hasta
              el seguimiento de pagos, gastos y reportes por cada edificio que administras.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {features.map(({ icon: Icon, title, description, accent, span }) => (
              <div
                key={title}
                data-feature-card
                className={`group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-7 sm:p-8 shadow-card hover:shadow-glow hover:border-rentahab-200 transition-all duration-500 ${span}`}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                />
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl bg-rentahab-50 text-rentahab-500 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg mb-2">{title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Planes */}
      <section id="planes" data-plans-section className="py-24 sm:py-32 bg-white relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(46,55,164,0.05)_0%,_transparent_55%)]" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center max-w-2xl mx-auto mb-14 sm:mb-16">
            <p className="text-rentahab-500 font-bold text-sm uppercase tracking-widest mb-3">
              Planes
            </p>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Elige el plan para tu operación
            </h2>
            <p className="mt-4 text-slate-500 text-lg leading-relaxed">
              El primer mes del plan Profesional es gratis. El límite es por edificio
              registrado — dentro de cada uno puedes cargar todos sus pisos y espacios.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch max-w-4xl mx-auto">
            {plans.map((plan) => {
              const copy = PLAN_COPY[plan.slug] || {
                tagline: 'Plan adaptado a tu negocio',
                cta: 'Solicitar plan',
                featured: false,
                trialMonths: 0,
              };
              const price = formatPlanPrice(plan.price_monthly);

              return (
                <article
                  key={plan.id || plan.slug}
                  data-plan-card
                  className={`relative flex flex-col rounded-3xl border p-7 sm:p-8 transition-all duration-300 ${
                    copy.featured
                      ? 'border-rentahab-400 bg-gradient-to-b from-rentahab-50 to-white shadow-glow md:scale-[1.02] z-10'
                      : 'border-slate-200 bg-white shadow-card hover:shadow-glow hover:border-rentahab-200'
                  }`}
                >
                  {copy.featured && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-rentahab-500 text-white text-[11px] font-bold uppercase tracking-wider px-4 py-1 rounded-full shadow-lg">
                      1 mes gratis
                    </span>
                  )}

                  <div className="mb-6">
                    <h3 className="text-xl font-black text-slate-900">{plan.name}</h3>
                    <p className="text-sm text-slate-500 mt-1">{copy.tagline}</p>
                  </div>

                  <div className="mb-6">
                    {copy.trialMonths ? (
                      <>
                        <p className="text-emerald-600 font-bold text-sm mb-2">
                          1er mes gratis · 1 edificio de prueba
                        </p>
                        <div className="flex items-end gap-1">
                          <span className="text-4xl font-black text-slate-900 tracking-tight">
                            {price.main}
                          </span>
                          <span className="text-slate-500 text-sm font-medium pb-1">{price.suffix}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          Desde el 2.º mes · hasta {plan.max_inmuebles} edificios en tu cuenta
                        </p>
                      </>
                    ) : (
                      <div className="flex items-end gap-1">
                        <span className="text-4xl font-black text-slate-900 tracking-tight">
                          {price.main}
                        </span>
                        {price.suffix && (
                          <span className="text-slate-500 text-sm font-medium pb-1">{price.suffix}</span>
                        )}
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {planFeatures(plan).map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <FiCheck className="w-4 h-4 text-rentahab-500 shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#contacto"
                    className={`inline-flex items-center justify-center gap-2 w-full font-bold py-3.5 rounded-full text-sm transition-all ${
                      copy.featured
                        ? 'bg-rentahab-500 text-white hover:bg-rentahab-600 shadow-lg shadow-rentahab-500/25'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    {copy.cta}
                    <FiArrowRight className="w-4 h-4" />
                  </a>
                </article>
              );
            })}
          </div>

          <p className="text-center text-sm text-slate-400 mt-10 max-w-2xl mx-auto leading-relaxed">
            Un edificio puede tener decenas de pisos y cientos de departamentos: el plan
            limita cuántos edificios administras, no cuántos espacios tiene cada uno.
            ¿Necesitas más edificios? Escríbenos y armamos un plan a medida.
          </p>
        </div>
      </section>

      {/* Mapa */}
      <section
        data-map-section
        className="py-24 sm:py-32 bg-slate-950 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(46,55,164,0.35)_0%,_transparent_55%)]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div data-map-content>
              <p data-reveal className="text-cyan-400 font-bold text-sm uppercase tracking-widest mb-3">
                Presencia en el país
              </p>
              <h2 data-reveal className="text-3xl sm:text-4xl font-black tracking-tight">
                Inmuebles que ya confían en nosotros
              </h2>
              <p data-reveal className="mt-4 text-slate-400 text-lg leading-relaxed max-w-md">
                Empresas y administradores que utilizan RentAHAB para gestionar sus
                propiedades en distintas ciudades del país.
              </p>
              <div data-reveal className="mt-10 grid grid-cols-3 gap-4">
                {[
                  { key: 'buildings', label: 'Edificios' },
                  { key: 'clients', label: 'Clientes' },
                  { key: 'cities', label: 'Ciudades' },
                ].map(({ key, label }) => (
                  <div
                    key={key}
                    className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm px-4 py-5 text-center"
                  >
                    <div
                      ref={setStatRef(key)}
                      className="text-3xl sm:text-4xl font-black text-white tabular-nums"
                    >
                      0
                    </div>
                    <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div
              data-map-visual
              className="h-[300px] sm:h-[380px] rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-rentahab-500/10 ring-1 ring-white/5"
            >
              <ClientsMap embedded onStatsChange={setMapStats} className="h-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section id="como-funciona" data-steps-section className="py-24 sm:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14 sm:mb-16">
            <p className="text-rentahab-500 font-bold text-sm uppercase tracking-widest mb-3">
              Proceso
            </p>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Cómo funciona
            </h2>
            <p className="mt-4 text-slate-500 text-lg leading-relaxed">
              Tres pasos para pasar del caos de planillas a una gestión clara de tus alquileres.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map(({ num, icon: Icon, title, text }, i) => (
              <div key={num} data-step-item className="relative">
                {i < steps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-10 left-[calc(50%+2.5rem)] w-[calc(100%-5rem)] h-px bg-gradient-to-r from-rentahab-200 to-rentahab-100 z-0"
                    aria-hidden
                  />
                )}
                <div className="relative z-10 h-full rounded-2xl border border-slate-200/80 bg-white p-7 sm:p-8 shadow-card hover:shadow-glow hover:border-rentahab-200 transition-all duration-300">
                  <div className="flex items-center gap-4 mb-5">
                    <span className="w-11 h-11 rounded-2xl bg-rentahab-500 text-white font-black text-sm flex items-center justify-center shadow-lg shadow-rentahab-500/25 shrink-0">
                      {num}
                    </span>
                    <div className="w-10 h-10 rounded-xl bg-rentahab-50 text-rentahab-500 flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg">{title}</h3>
                  <p className="text-slate-500 mt-2 leading-relaxed text-sm">{text}</p>
                </div>
              </div>
            ))}
          </div>

          <div
            data-arch-card
            className="mt-10 sm:mt-12 relative rounded-3xl overflow-hidden border border-rentahab-200/60 bg-gradient-to-br from-slate-900 via-rentahab-900 to-slate-900 px-6 py-8 sm:px-10 sm:py-10 text-white"
          >
            <div className="absolute top-0 right-0 w-72 h-72 bg-rentahab-500/15 rounded-full blur-3xl pointer-events-none" />
            <div className="relative grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-cyan-300/80 mb-3">
                  Para tu operación diaria
                </p>
                <h3 className="font-black text-xl sm:text-2xl leading-snug">
                  Menos tiempo buscando información, más control sobre cada propiedad
                </h3>
                <p className="text-slate-300 mt-3 text-sm sm:text-base leading-relaxed">
                  RentAHAB concentra en un solo lugar lo que hoy repartes entre planillas,
                  carpetas y mensajes.
                </p>
              </div>
              <ul className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                {serviceHighlights.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3.5 text-sm text-slate-100"
                  >
                    <FiCheck className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        id="contacto"
        data-cta-section
        className="py-24 sm:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-rentahab-900 to-slate-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(46,55,164,0.4)_0%,_transparent_70%)]" />

        <div data-cta-content className="max-w-3xl mx-auto px-6 text-center relative">
          <h2 data-reveal className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
            ¿Quieres simplificar la gestión de tus alquileres?
          </h2>
          <p data-reveal className="mt-5 text-slate-400 text-lg leading-relaxed">
            Si administras edificios, departamentos o locales en alquiler, RentAHAB te ayuda
            a tener todo organizado: inquilinos, pagos, gastos y reportes en un solo sistema.
          </p>
          <Link
            to="/login"
            data-reveal
            className="mt-10 inline-flex items-center gap-2 bg-white text-rentahab-700 font-bold px-10 py-4 rounded-full hover:shadow-glow hover:scale-[1.03] transition-all text-base"
          >
            Solicitar acceso
            <FiArrowRight className="w-5 h-5" />
          </Link>
          <p data-reveal className="mt-5">
            <Link
              to="/login"
              className="text-sm text-rentahab-300 hover:text-white transition-colors"
            >
              ¿Ya eres cliente? Ingresa aquí →
            </Link>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-300 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 md:gap-8">
            <div>
              <div className="inline-flex items-center bg-white rounded-xl px-3 py-2 mb-4">
                <BrandLogo />
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Servicio de gestión integral de alquileres para inmobiliarias y administradores
                de edificios. Inmuebles, inquilinos, pagos y reportes en un solo lugar.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-white text-sm mb-4 uppercase tracking-wider">Enlaces</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="#servicios" className="text-slate-400 hover:text-white transition-colors">
                    Servicios
                  </a>
                </li>
                <li>
                  <a href="#planes" className="text-slate-400 hover:text-white transition-colors">
                    Planes
                  </a>
                </li>
                <li>
                  <a href="#como-funciona" className="text-slate-400 hover:text-white transition-colors">
                    Cómo funciona
                  </a>
                </li>
                <li>
                  <Link to="/login" className="text-slate-400 hover:text-white transition-colors">
                    Acceso admin
                  </Link>
                </li>
              </ul>
            </div>

            <div className="sm:col-span-2 md:col-span-1">
              <h4 className="font-bold text-white text-sm mb-4 uppercase tracking-wider">
                Desarrollado por
              </h4>
              <a
                href="https://hakutec.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-bold text-white hover:text-rentahab-300 text-lg transition-colors"
              >
                HAKUTEC
                <FiExternalLink className="w-3.5 h-3.5 opacity-60" />
              </a>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                Soluciones TI sin límites. Convierte tus ideas en productos tecnológicos.
              </p>
              <div className="flex gap-2 mt-4">
                <a
                  href="https://www.linkedin.com/company/hakutec"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center text-slate-400 hover:bg-rentahab-500 hover:text-white transition-colors"
                  aria-label="LinkedIn HAKUTEC"
                >
                  <FiLinkedin className="w-4 h-4" />
                </a>
                <a
                  href="https://hakutec.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center text-slate-400 hover:bg-rentahab-500 hover:text-white transition-colors"
                  aria-label="Web HAKUTEC"
                >
                  <FiGlobe className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-5 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} RentAHAB. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PlatformLanding;
